import React, { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { projects } from '../assets/projects';
import { initInteriorLightbox } from '../utils/initInteriorLightbox';
import useReveal from '../hooks/useReveal';
import {
  gsap,
  setupGsap,
  prefersReducedMotion,
} from '../motion/gsapSetup';
import { useSmoothScroll } from '../motion/SmoothScroll';

const SingleProject = () => {
  const { id } = useParams();
  const galleryRef = useRef(null);
  const sectionRef = useRef(null);
  const featureRef = useRef(null);
  const { lenis } = useSmoothScroll() || {};

  const projectIndex = projects.findIndex((item) => String(item.id) === String(id));
  const project = projectIndex !== -1 ? projects[projectIndex] : projects[0];

  const prevProject = projectIndex > 0 ? projects[projectIndex - 1] : projects[projects.length - 1];
  const nextProject = projectIndex < projects.length - 1 ? projects[projectIndex + 1] : projects[0];

  const galleryImages = project.gallery || [project.image];
  const relatedProjects = projects
    .filter((item) => item.id !== project.id)
    .slice(0, 3);

  useReveal(sectionRef, '.project-stat-pill, .project-spec-item, .project-gallery-item, .related-project-card-v2', {
    y: 32,
    stagger: 0.06,
  });

  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [project.id, lenis]);

  useEffect(
    () => initInteriorLightbox(galleryRef.current, 'a.project-zoom'),
    [project.id]
  );

  useEffect(() => {
    setupGsap();
    const feature = featureRef.current;
    if (!feature || prefersReducedMotion()) return undefined;
    const img = feature.querySelector('.project-hero-img');
    if (!img) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        img,
        { scale: 1.08 },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: feature,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    }, feature);

    return () => ctx.revert();
  }, [project.id]);

  return (
    <>
      {/* Editorial Case Study Header */}
      <section className="project-editorial-header">
        <div className="apple-wrap--wide">
          <div className="project-header-top-nav">
            <Link to="/portfolio" className="project-back-link">
              <i className="fas fa-arrow-left" aria-hidden />
              <span>All Projects</span>
            </Link>
            <div className="project-archive-badge">
              <span className="pulse-dot" />
              <span>Archival Case Study #{project.number}</span>
            </div>
          </div>

          <div className="project-title-block">
            <div className="project-meta-pill-row">
              <span className="project-cat-pill">{project.category || 'Architecture'}</span>
              <span className="project-loc-pill">
                <i className="fas fa-map-pin" aria-hidden />
                {project.location}
              </span>
            </div>

            <h1 className="project-headline">
              {project.title}
            </h1>

            <p className="project-intro-lede">
              A bespoke private residence harmonizing sculpted structural curves, material warmth, and filtered atmospheric light into a private urban sanctuary.
            </p>
          </div>
        </div>
      </section>

      <section className="single-project-section" ref={sectionRef}>
        <div className="apple-wrap--wide">
          {/* Main Cinematic Feature Frame */}
          <div className="project-hero-frame" ref={featureRef}>
            <div className="project-hero-media-wrap">
              <img
                src={project.image}
                alt={project.title}
                className="project-hero-img"
              />
              <div className="project-hero-gradient-scrim" />

              {/* Floating Hero Badges */}
              <div className="project-hero-floating-tag">
                <div className="hero-tag-icon">
                  <i className="fas fa-building" aria-hidden />
                </div>
                <div className="hero-tag-copy">
                  <span className="tag-label">TheStudioVS Build</span>
                  <span className="tag-val">Turnkey Residential</span>
                </div>
              </div>

              <a
                href={project.image}
                className="project-zoom hero-expand-btn"
                title="Expand main elevation view"
              >
                <i className="fas fa-expand-alt" aria-hidden />
                <span>Enlarge View</span>
              </a>
            </div>
          </div>

          {/* Architectural Specifications Grid Strip */}
          <div className="project-specs-deck">
            <div className="project-spec-item">
              <span className="spec-label">Location</span>
              <span className="spec-value">{project.location}</span>
            </div>
            <div className="project-spec-item">
              <span className="spec-label">Typology</span>
              <span className="spec-value">Private Residential</span>
            </div>
            <div className="project-spec-item">
              <span className="spec-label">Discipline</span>
              <span className="spec-value">Architecture & Interiors</span>
            </div>
            <div className="project-spec-item">
              <span className="spec-label">Delivery</span>
              <span className="spec-value">Turnkey Handover</span>
            </div>
          </div>

          {/* Architectural Brief & Philosophy Section */}
          <div className="project-narrative-card">
            <div className="project-narrative-copy">
              <span className="apple-eyebrow">Design Narrative</span>
              <h2 className="narrative-statement">
                Architecture shaped as an <span className="apple-italic">experience</span>, engineered for lasting peace.
              </h2>
              <p className="narrative-body">
                From the perforated kinetic screen that responds to southern sun angles to the custom cantilevered balconies, each level is orchestrated to balance privacy from the street while unlocking expansive, light-filled interior volumes.
              </p>
              
              <div className="project-features-chips">
                <span className="feature-chip">
                  <i className="fas fa-sun" aria-hidden /> Daylight Wells
                </span>
                <span className="feature-chip">
                  <i className="fas fa-cubes" aria-hidden /> Custom Facade Jali
                </span>
                <span className="feature-chip">
                  <i className="fas fa-gem" aria-hidden /> Italian Travertine
                </span>
                <span className="feature-chip">
                  <i className="fas fa-tree" aria-hidden /> Thermal Timber
                </span>
              </div>
            </div>

            <div className="project-narrative-quote-box">
              <div className="quote-mark-icon">
                <i className="fas fa-quote-left" aria-hidden />
              </div>
              <blockquote className="quote-text">
                "We don't merely design walls and facades — we orchestrate how morning light enters your living room and how space breathes."
              </blockquote>
              <div className="quote-author-row">
                <span className="author-name">TheStudioVS</span>
                <span className="author-role">Principal Architects & Builders</span>
              </div>
            </div>
          </div>

          {/* Selected Architectural Views Gallery */}
          <div className="project-gallery-block">
            <div className="gallery-header-row">
              <div>
                <span className="apple-eyebrow">Archival Documentation</span>
                <h3 className="apple-display" style={{ fontSize: 'clamp(28px, 4vw, 42px)', margin: '4px 0 0' }}>
                  Selected Views & Details
                </h3>
              </div>
              <span className="gallery-count-badge">
                <i className="fas fa-images" aria-hidden /> {galleryImages.length} Frame{galleryImages.length > 1 ? 's' : ''}
              </span>
            </div>

            <div ref={galleryRef} className="project-gallery-editorial-grid">
              {galleryImages.map((image, index) => (
                <div key={`${project.id}-${index}`} className="project-gallery-item">
                  <a
                    href={image}
                    className="project-zoom gallery-card-link"
                    title={`${project.title} — Detail View ${index + 1}`}
                  >
                    <div className="gallery-img-wrap">
                      <img
                        src={image}
                        alt={`${project.title} view ${index + 1}`}
                        loading="lazy"
                        className="gallery-thumbnail"
                      />
                      <div className="gallery-hover-overlay">
                        <div className="gallery-zoom-icon">
                          <i className="fas fa-search-plus" aria-hidden />
                        </div>
                        <span className="gallery-view-index">View #{String(index + 1).padStart(2, '0')}</span>
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Previous / Next Project Switcher Strip */}
          <div className="project-pager-strip">
            <Link to={prevProject.link} className="project-pager-btn prev">
              <div className="pager-arrow">
                <i className="fas fa-arrow-left" aria-hidden />
              </div>
              <div className="pager-info">
                <span className="pager-dir">Previous Work</span>
                <span className="pager-title">{prevProject.title}</span>
              </div>
            </Link>

            <Link to="/portfolio" className="project-pager-grid-btn" title="View all portfolio projects">
              <i className="fas fa-th-large" aria-hidden />
            </Link>

            <Link to={nextProject.link} className="project-pager-btn next">
              <div className="pager-info">
                <span className="pager-dir">Next Work</span>
                <span className="pager-title">{nextProject.title}</span>
              </div>
              <div className="pager-arrow">
                <i className="fas fa-arrow-right" aria-hidden />
              </div>
            </Link>
          </div>

          {/* Related Projects Deck */}
          {relatedProjects.length > 0 && (
            <div className="project-related-section">
              <div className="related-head-row">
                <div>
                  <span className="apple-eyebrow">Explore More</span>
                  <h3 className="apple-display" style={{ fontSize: 'clamp(26px, 3.5vw, 36px)', margin: '4px 0 0' }}>
                    Related Case Studies
                  </h3>
                </div>
                <Link to="/portfolio" className="related-all-link">
                  <span>View All Projects</span>
                  <i className="fas fa-arrow-right" aria-hidden />
                </Link>
              </div>

              <div className="project-related-grid">
                {relatedProjects.map((item) => (
                  <Link
                    key={item.id}
                    to={item.link}
                    className="related-project-card-v2"
                  >
                    <div className="related-media-box">
                      <img src={item.image} alt={item.title} loading="lazy" />
                      <div className="related-scrim" />
                      <span className="related-num-badge">{item.number}</span>
                    </div>
                    <div className="related-card-details">
                      <span className="related-loc">{item.location}</span>
                      <h4 className="related-title">{item.title}</h4>
                      <div className="related-cta-arrow">
                        <i className="fas fa-arrow-right" aria-hidden />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Bottom Inquiries Consultation Card */}
          <div className="project-inquiry-card">
            <div className="inquiry-card-copy">
              <span className="inquiry-eyebrow">Have a residential plot or villa project?</span>
              <h3>Bring architectural distinction to your next build.</h3>
              <p>
                From structural blueprints and municipal approvals to turnkey interior execution, our studio manages every phase.
              </p>
            </div>
            <a href="/#contact" className="inquiry-btn">
              <span>Schedule Architecture Consultation</span>
              <i className="fas fa-arrow-right" aria-hidden />
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default SingleProject;
